import type { ComponentChildren, JSX } from 'preact';
import { ChevronLeft, LoaderCircle, Plus, RefreshCw, WifiOff, X } from 'lucide-preact';
import type { ClanEmblem, Currency, Presence, Rarity, RewardBundle } from '@vm/shared';
import { audio } from '../../game/audio/Audio';
import { itemIcon, itemInfo, RARITY_LABEL } from '../state/content';
import { back, go, openModal } from '../state/nav';
import { retryOnline } from '../state/session';
import { isOffline, wallet } from '../state/store';
import { fmtCompact, fmtNum, fmtRemaining, hex, pct } from './format';
import { useBusy, useNow } from './hooks';
import { EMBLEM_ICONS, Icon } from './icons';
import { skinSprite, weaponIcon } from '@vm/content/sprites';

// Currency --------------------------------------------------------------------------------------------------

export function CoinIcon(props: { size?: number }) {
  const s = props.size ?? 20;
  return (
    <svg class="ui-coin" width={s} height={s} viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12.8" r="10" fill="#b8840f" />
      <circle cx="12" cy="11.4" r="10" fill="#ffd24a" />
      <circle cx="12" cy="11.4" r="7" fill="#f5b82a" />
      <path d="M8.6 8.2h2.3l1.1 4.1 1.1-4.1h2.3l-2.3 6.6h-2.2z" fill="#fff4c2" />
    </svg>
  );
}

export function GemIcon(props: { size?: number }) {
  const s = props.size ?? 20;
  return (
    <svg class="ui-gem" width={s} height={s} viewBox="0 0 24 24" aria-hidden="true">
      <path d="M6 3.5h12l4.5 6L12 22 1.5 9.5z" fill="#1aa890" />
      <path d="M6 3.5h12l4.5 6H1.5z" fill="#4af0d0" />
      <path d="M8.5 9.5 12 22l3.5-12.5z" fill="#7affe6" />
      <path d="M6 3.5 8.5 9.5h7L18 3.5z" fill="#b0fff0" />
    </svg>
  );
}

export function CurrencyIcon(props: { kind: Currency; size?: number }) {
  return props.kind === 'gems' ? <GemIcon size={props.size} /> : <CoinIcon size={props.size} />;
}

export function Price(props: { price: number; currency: Currency; class?: string }) {
  return (
    <span class={`ui-price ${props.currency} ${props.class ?? ''}`}>
      <CurrencyIcon kind={props.currency} size={18} />
      {props.price === 0 ? 'Free' : fmtNum(props.price)}
    </span>
  );
}

export function CurrencyPill(props: { kind: Currency; plus?: boolean }) {
  const w = wallet.value;
  const v = props.kind === 'gems' ? w.gems : w.coins;
  return (
    <div class={`ui-pill ${props.kind}`} title={props.kind === 'gems' ? 'Gems' : 'Coins'}>
      <CurrencyIcon kind={props.kind} size={24} />
      <b>{fmtCompact(v)}</b>
      {props.plus && !isOffline.value && (
        <button class="ui-pill-plus" aria-label="Get gems" onClick={() => go('store', { tab: 'gems' })}>
          <Plus size={18} strokeWidth={3.5} />
        </button>
      )}
    </div>
  );
}

export function WalletPills() {
  return (
    <div class="ui-wallet">
      <CurrencyPill kind="coins" />
      <CurrencyPill kind="gems" plus />
    </div>
  );
}

// Layout -----------------------------------------------------------------------------------------------------

export function Screen(props: { title: string; icon?: ComponentChildren; right?: ComponentChildren; tabs?: ComponentChildren; children: ComponentChildren; class?: string; onBack?: () => void }) {
  return (
    <div class={`scr ${props.class ?? ''}`}>
      <header class={`scr-head ${props.tabs ? 'has-tabs' : ''}`}>
        <button class="scr-back" onClick={props.onBack ?? back} aria-label="Back">
          <ChevronLeft size={30} strokeWidth={3} />
        </button>
        <h1 class="scr-title">
          {props.icon && <span class="scr-title-icon">{props.icon}</span>}
          <span>{props.title}</span>
        </h1>
        {props.tabs && <div class="scr-tabs">{props.tabs}</div>}
        <div class="scr-right">{props.right ?? <WalletPills />}</div>
      </header>
      <div class="scr-body">{props.children}</div>
    </div>
  );
}

export interface TabDef<T extends string> {
  id: T;
  label: string;
  icon?: ComponentChildren;
  badge?: number;
  disabled?: boolean;
}

export function Tabs<T extends string>(props: { tabs: TabDef<T>[]; value: T; onChange: (id: T) => void; class?: string }) {
  return (
    <div class={`ui-tabs ${props.class ?? ''}`} role="tablist">
      {props.tabs.map((t) => (
        <button
          key={t.id}
          role="tab"
          aria-selected={t.id === props.value}
          class={`ui-tab ${t.id === props.value ? 'is-on' : ''}`}
          disabled={t.disabled}
          onClick={() => {
            if (t.id === props.value) return;
            audio.play('ui_click');
            props.onChange(t.id);
          }}
        >
          {t.icon}
          <span>{t.label}</span>
          {!!t.badge && <Badge count={t.badge} />}
        </button>
      ))}
    </div>
  );
}

export function Segmented<T extends string | number>(props: { options: { value: T; label: string }[]; value: T; onChange: (v: T) => void }) {
  return (
    <div class="ui-seg">
      {props.options.map((o) => (
        <button
          key={String(o.value)}
          class={o.value === props.value ? 'is-on' : ''}
          onClick={() => {
            audio.play('ui_click');
            props.onChange(o.value);
          }}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function Badge(props: { count: number; class?: string }) {
  if (!props.count) return null;
  return <span class={`ui-badge ${props.class ?? ''}`}>{props.count > 99 ? '99+' : props.count}</span>;
}

export function Dot() {
  return <span class="ui-dot" />;
}

// Feedback ---------------------------------------------------------------------------------------------------

export function Spinner(props: { label?: string; class?: string }) {
  return (
    <div class={`ui-spinner ${props.class ?? ''}`}>
      <LoaderCircle size={34} class="ui-spin" strokeWidth={2.5} />
      {props.label && <span>{props.label}</span>}
    </div>
  );
}

export function Empty(props: { icon?: ComponentChildren; title: string; body?: ComponentChildren; children?: ComponentChildren; class?: string }) {
  return (
    <div class={`ui-empty ${props.class ?? ''}`}>
      {props.icon && <div class="ui-empty-icon">{props.icon}</div>}
      <h3>{props.title}</h3>
      {props.body && <p>{props.body}</p>}
      {props.children}
    </div>
  );
}

export function ErrorState(props: { message: string; onRetry?: () => void }) {
  return (
    <Empty icon={<WifiOff size={40} />} title="Couldn't load this" body={props.message}>
      {props.onRetry && (
        <button class="btn" onClick={props.onRetry}>
          <RefreshCw size={18} /> Try again
        </button>
      )}
    </Empty>
  );
}

/** Replaces online-only content with an offline notice + retry. */
export function OnlineOnly(props: { feature: string; children: ComponentChildren }) {
  const [busy, run] = useBusy();
  if (!isOffline.value) return <>{props.children}</>;
  return (
    <Empty icon={<WifiOff size={44} />} title="You're offline" body={`${props.feature} needs a connection to the Voxel Marines servers. Practice, Boot Camp and the Sandbox still work.`}>
      <button class="btn primary" disabled={busy} onClick={() => run(retryOnline)}>
        <RefreshCw size={18} class={busy ? 'ui-spin' : ''} /> {busy ? 'Connecting…' : 'Retry'}
      </button>
    </Empty>
  );
}

export function Bar(props: { value: number; max: number; tone?: 'xp' | 'green' | 'blue' | 'purple' | 'gem' | 'orange'; label?: ComponentChildren; class?: string }) {
  return (
    <div class={`ui-bar ${props.tone ?? 'xp'} ${props.class ?? ''}`}>
      <div class="ui-bar-fill" style={{ width: `${pct(props.value, props.max)}%` }} />
      {props.label !== undefined && <div class="ui-bar-label">{props.label}</div>}
    </div>
  );
}

export function RarityTag(props: { rarity: Rarity }) {
  return <span class={`ui-rarity r-${props.rarity}`}>{RARITY_LABEL[props.rarity]}</span>;
}

export function Countdown(props: { to: string | number; prefix?: string; class?: string }) {
  const now = useNow(1000);
  const t = typeof props.to === 'number' ? props.to : Date.parse(props.to);
  const left = t - now;
  return (
    <span class={`ui-countdown ${props.class ?? ''}`}>
      {props.prefix}
      {left > 0 ? fmtRemaining(left) : 'Ended'}
    </span>
  );
}

export function LevelBadge(props: { level: number; size?: 'sm' | 'md' | 'lg' }) {
  return (
    <div class={`ui-level ${props.size ?? 'md'}`} aria-label={`Level ${props.level}`}>
      <svg viewBox="0 0 40 44" aria-hidden="true">
        <path d="M20 1.5 37.5 11v22L20 42.5 2.5 33V11z" fill="#2a64d8" stroke="#0e2a6a" stroke-width="3" />
        <path d="M20 5.5 34 13v9.5H6V13z" fill="#4a8aff" opacity="0.55" />
      </svg>
      <b>{props.level}</b>
    </div>
  );
}

export function PresenceDot(props: { presence: Presence }) {
  return <span class={`ui-presence p-${props.presence}`} title={presenceLabel(props.presence)} />;
}

export function presenceLabel(p: Presence, activity?: string): string {
  if (p === 'in_match') return activity ? `Playing ${activity}` : 'In a match';
  if (p === 'lobby') return 'In the lobby';
  if (p === 'away') return 'Away';
  return 'Offline';
}

// Images ------------------------------------------------------------------------------------------------------

export function SkinImg(props: { skin: string; armor: string; mode?: 'full' | 'head'; class?: string; scale?: number; bare?: boolean }) {
  const url = skinSprite(props.skin, props.armor, props.mode ?? 'full', props.scale ?? 6, props.bare);
  return <img class={`ui-sprite ${props.class ?? ''}`} src={url} alt="" draggable={false} />;
}

export function WeaponImg(props: { weaponId: string; camo?: string; scope?: string; class?: string; scale?: number }) {
  const url = weaponIcon(props.weaponId, props.camo, props.scale ?? 4, props.scope);
  if (!url) return <span class={`ui-sprite ${props.class ?? ''}`} />;
  return <img class={`ui-sprite ${props.class ?? ''}`} src={url} alt="" draggable={false} />;
}

const AVATAR_COLORS = ['#ff5a4a', '#ff9a3a', '#ffd24a', '#52d86a', '#4af0d0', '#4a9aff', '#b46aff', '#ff6ab0'];

export function Avatar(props: { name: string; skin?: string; armor?: string; size?: number; presence?: Presence; class?: string }) {
  const size = props.size ?? 40;
  let h = 0;
  for (const ch of props.name) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return (
    <div class={`ui-avatar ${props.class ?? ''}`} style={{ width: `${size}px`, height: `${size}px`, background: props.skin ? undefined : AVATAR_COLORS[h % AVATAR_COLORS.length] }}>
      {props.skin ? <SkinImg skin={props.skin} armor={props.armor ?? 'standard'} mode="head" scale={4} /> : <span style={{ fontSize: `${Math.round(size * 0.46)}px` }}>{props.name.slice(0, 1).toUpperCase()}</span>}
      {props.presence && <PresenceDot presence={props.presence} />}
    </div>
  );
}

// Clan emblems -------------------------------------------------------------------------------------------------

export const EMBLEM_COLORS = [
  0xff4a4a, 0xff8a2a, 0xffd23a, 0x9ae83a, 0x3ac85a, 0x2ad8b0, 0x3ac8f0, 0x3a7aff, 0x5a4aff, 0xa04aff, 0xff5ab8, 0x8a5a3a, 0x8890a0, 0x2a2e3a,
  0xf4f6fa, 0xe8b830,
];

export const EMBLEM_SHAPES: { name: string; d: string }[] = [
  { name: 'Shield', d: 'M32 3 57 12v18c0 16-11 27-25 31C18 57 7 46 7 30V12z' },
  { name: 'Circle', d: 'M32 4a28 28 0 1 1 0 56a28 28 0 1 1 0-56z' },
  { name: 'Hexagon', d: 'M32 3 58 18v28L32 61 6 46V18z' },
  { name: 'Diamond', d: 'M32 2 62 32 32 62 2 32z' },
  { name: 'Square', d: 'M16 5h32a11 11 0 0 1 11 11v32a11 11 0 0 1-11 11H16A11 11 0 0 1 5 48V16A11 11 0 0 1 16 5z' },
  { name: 'Badge', d: 'M21 3h22l18 18v22L43 61H21L3 43V21z' },
  { name: 'Banner', d: 'M9 3h46v56L32 47 9 59z' },
];

/** Emblem colors are 0xRRGGBB (small values are treated as palette indices for older data). */
function emblemColor(v: number): number {
  return v >= 0 && v < EMBLEM_COLORS.length ? EMBLEM_COLORS[v] : v & 0xffffff;
}

export function Emblem(props: { emblem: ClanEmblem; size?: number; class?: string }) {
  const s = props.size ?? 56;
  const e = props.emblem;
  const shape = EMBLEM_SHAPES[e.shape] ?? EMBLEM_SHAPES[0];
  const bg = hex(emblemColor(e.color));
  const fg = hex(emblemColor(e.iconColor));
  const gid = `eg${e.shape}-${e.color}`;
  return (
    <div class={`ui-emblem ${props.class ?? ''}`} style={{ width: `${s}px`, height: `${s}px` }}>
      <svg viewBox="0 0 64 64" width={s} height={s} aria-hidden="true">
        <defs>
          <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stop-color="#fff" stop-opacity="0.35" />
            <stop offset="0.55" stop-color="#fff" stop-opacity="0" />
            <stop offset="1" stop-color="#000" stop-opacity="0.25" />
          </linearGradient>
        </defs>
        <path d={shape.d} fill={bg} stroke="rgba(0,0,0,0.45)" stroke-width="3" />
        <path d={shape.d} fill={`url(#${gid})`} />
      </svg>
      <span class="ui-emblem-icon" style={{ color: fg }}>
        <Icon name={EMBLEM_ICONS[e.icon] ?? 'shield'} size={Math.round(s * 0.46)} strokeWidth={2.6} />
      </span>
    </div>
  );
}

// Rewards ------------------------------------------------------------------------------------------------------

export function RewardChips(props: { reward: RewardBundle | null | undefined; class?: string }) {
  const r = props.reward;
  if (!r) return null;
  return (
    <div class={`ui-rewards ${props.class ?? ''}`}>
      {!!r.coins && (
        <span class="ui-reward coins">
          <CoinIcon size={18} />
          {fmtNum(r.coins)}
        </span>
      )}
      {!!r.gems && (
        <span class="ui-reward gems">
          <GemIcon size={18} />
          {fmtNum(r.gems)}
        </span>
      )}
      {!!r.xp && <span class="ui-reward xp">+{fmtNum(r.xp)} XP</span>}
      {!!r.bpXp && <span class="ui-reward passxp">+{fmtNum(r.bpXp)} Pass XP</span>}
      {(r.items ?? []).map((it) => {
        const info = itemInfo(it.type, it.id);
        return (
          <span key={`${it.type}:${it.id}`} class={`ui-reward item r-${info.rarity}`}>
            {it.type === 'weapon' ? <WeaponImg weaponId={it.id} scale={2} class="ui-reward-img" /> : <Icon name={itemIcon(it.type, it.id)} size={16} />}
            {info.name}
          </span>
        );
      })}
    </div>
  );
}

// Inputs ---------------------------------------------------------------------------------------------------------

export function Toggle(props: { checked: boolean; onChange: (v: boolean) => void; label: ComponentChildren; hint?: string; disabled?: boolean }) {
  return (
    <label class={`ui-toggle ${props.disabled ? 'is-disabled' : ''}`}>
      <span class="ui-field-text">
        <span>{props.label}</span>
        {props.hint && <small>{props.hint}</small>}
      </span>
      <input
        type="checkbox"
        checked={props.checked}
        disabled={props.disabled}
        onChange={(e) => {
          audio.play('ui_click');
          props.onChange((e.currentTarget as HTMLInputElement).checked);
        }}
      />
      <span class="ui-switch" aria-hidden="true" />
    </label>
  );
}

export function Slider(props: { label: ComponentChildren; value: number; min: number; max: number; step: number; onChange: (v: number) => void; format?: (v: number) => string; hint?: string }) {
  const p = ((props.value - props.min) / (props.max - props.min)) * 100;
  return (
    <label class="ui-slider">
      <span class="ui-field-text">
        <span>{props.label}</span>
        {props.hint && <small>{props.hint}</small>}
      </span>
      <b class="ui-slider-val">{props.format ? props.format(props.value) : props.value}</b>
      <input
        type="range"
        min={props.min}
        max={props.max}
        step={props.step}
        value={props.value}
        style={{ '--p': `${p}%` } as Record<string, string>}
        onInput={(e) => props.onChange(Number((e.currentTarget as HTMLInputElement).value))}
      />
    </label>
  );
}

export function Field(props: { label: string; hint?: ComponentChildren; error?: string | null; children: ComponentChildren; class?: string }) {
  return (
    <label class={`ui-field ${props.error ? 'has-error' : ''} ${props.class ?? ''}`}>
      <span class="ui-field-label">{props.label}</span>
      {props.children}
      {props.error ? <small class="ui-field-error">{props.error}</small> : props.hint ? <small class="ui-field-hint">{props.hint}</small> : null}
    </label>
  );
}

/** A button that runs an async action and shows a spinner meanwhile. */
export function AsyncButton(props: { onClick: () => Promise<unknown>; class?: string; disabled?: boolean; children: ComponentChildren; title?: string; 'aria-label'?: string }) {
  const [busy, run] = useBusy();
  return (
    <button class={`btn ${props.class ?? ''} ${busy ? 'is-busy' : ''}`} disabled={props.disabled || busy} title={props.title} aria-label={props['aria-label']} onClick={() => run(props.onClick)}>
      {busy && <LoaderCircle size={18} class="ui-spin" />}
      {props.children}
    </button>
  );
}

// Modals -------------------------------------------------------------------------------------------------------------

export function Modal(props: { title?: ComponentChildren; icon?: ComponentChildren; onClose?: () => void; children: ComponentChildren; size?: 'sm' | 'md' | 'lg' | 'xl'; class?: string; hideClose?: boolean }) {
  return (
    <div class={`ui-modal ${props.size ?? 'md'} ${props.class ?? ''}`} role="dialog" aria-modal="true" onClick={(e: JSX.TargetedMouseEvent<HTMLDivElement>) => e.stopPropagation()}>
      {(props.title || props.onClose) && (
        <div class="ui-modal-head">
          {props.icon && <span class="ui-modal-icon">{props.icon}</span>}
          <h2>{props.title}</h2>
          {props.onClose && !props.hideClose && (
            <button
              class="ui-close"
              aria-label="Close"
              onClick={() => {
                audio.play('ui_click');
                props.onClose!();
              }}
            >
              <X size={22} strokeWidth={3} />
            </button>
          )}
        </div>
      )}
      <div class="ui-modal-body">{props.children}</div>
    </div>
  );
}

export interface ConfirmOptions {
  title: string;
  body?: ComponentChildren;
  confirm?: string;
  cancel?: string;
  danger?: boolean;
  icon?: ComponentChildren;
}

export function confirmDialog(o: ConfirmOptions): Promise<boolean> {
  return new Promise((resolve) => {
    openModal(
      (close) => (
        <Modal
          title={o.title}
          icon={o.icon}
          size="sm"
          onClose={() => {
            close();
            resolve(false);
          }}
        >
          {o.body && <div class="ui-confirm-body">{o.body}</div>}
          <div class="ui-modal-actions">
            <button
              class="btn"
              onClick={() => {
                close();
                resolve(false);
              }}
            >
              {o.cancel ?? 'Cancel'}
            </button>
            <button
              class={`btn ${o.danger ? 'danger' : 'primary'}`}
              onClick={() => {
                close();
                resolve(true);
              }}
            >
              {o.confirm ?? 'OK'}
            </button>
          </div>
        </Modal>
      ),
      { onDismiss: () => resolve(false) },
    );
  });
}

export function alertDialog(title: string, body: ComponentChildren, icon?: ComponentChildren): void {
  openModal((close) => (
    <Modal title={title} icon={icon} size="sm" onClose={close}>
      <div class="ui-confirm-body">{body}</div>
      <div class="ui-modal-actions">
        <button class="btn primary" onClick={close}>
          OK
        </button>
      </div>
    </Modal>
  ));
}
